/**
 * ScenarioLab Results Components
 * 
 * Components for displaying scenario run results including summary,
 * advisory processing, risk assessment, provenance, and metadata.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { 
  Train,
  AlertTriangle,
  Shield,
  FileCheck
} from 'lucide-react';
import { 
  getRecommendationCardStyle, 
  getRecommendationBgStyle, 
  getRecommendationTextStyle,
  getConfidenceStyle,
  getRiskStyle
} from './shared';

// Summary Card - Main recommendation display
export const SummaryCard = ({ result }) => (
  <Card className={`border-2 ${getRecommendationCardStyle(result.recommendation)}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${getRecommendationBgStyle(result.recommendation)}`}>
            <Train className={`w-5 h-5 ${getRecommendationTextStyle(result.recommendation)}`} />
          </div>
          <div>
            <div className="text-xs text-slate-400">Recommendation</div>
            <div className="text-xl font-bold text-white uppercase">{result.recommendation}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-400">Confidence</div>
          <Badge className={getConfidenceStyle(result.confidence_level)}>
            {result.confidence_level.toUpperCase()}
          </Badge>
        </div>
      </div>
      <p className="text-slate-300 text-sm">{result.recommendation_rationale}</p>
    </CardContent>
  </Card>
);

// Advisory Processing Card
export const AdvisoryProcessingCard = ({ result }) => (
  <Card className="bg-slate-800/50 border-slate-700">
    <CardHeader className="pb-2">
      <CardTitle className="text-white text-base">Advisory Processing</CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center p-3 bg-slate-900/50 rounded-lg">
          <div className="text-2xl font-bold text-white">{result.advisories_received}</div>
          <div className="text-xs text-slate-400">Received</div>
        </div>
        <div className="text-center p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
          <div className="text-2xl font-bold text-emerald-400">{result.advisories_accepted}</div>
          <div className="text-xs text-emerald-400">Accepted</div>
        </div>
        <div className="text-center p-3 bg-red-500/10 rounded-lg border border-red-500/30">
          <div className="text-2xl font-bold text-red-400">{result.advisories_excluded}</div>
          <div className="text-xs text-red-400">Excluded</div>
        </div>
      </div>
      
      {result.excluded_details.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-red-400">Excluded Advisories:</div>
          {result.excluded_details.map((exc) => (
            <div key={exc.advisory_id} className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-white text-sm">{exc.source}</span>
                <Badge className="bg-red-600 text-xs">EXCLUDED</Badge>
              </div>
              <p className="text-slate-400 text-xs mb-1">"{exc.content_preview}"</p>
              <p className="text-red-400 text-xs">{exc.reason}</p>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

// Risk Assessment Card
export const RiskAssessmentCard = ({ result }) => (
  <Card className="bg-slate-800/50 border-slate-700">
    <CardHeader className="pb-2">
      <CardTitle className="text-white text-base flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-400" />
        Risk Assessment
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-slate-400 text-sm">Original Journey:</span>
          <Badge className={getRiskStyle(result.risk_original)}>
            {result.risk_original}
          </Badge>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-slate-400 text-sm">Recommended:</span>
          <Badge className={getRiskStyle(result.risk_recommended)}>
            {result.risk_recommended}
          </Badge>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Provenance Card
export const ProvenanceCard = ({ result }) => (
  <Card className={`border ${result.katlas_signed ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-800/50 border-slate-700'}`}>
    <CardHeader className="pb-2">
      <CardTitle className="text-white text-base flex items-center gap-2">
        <Shield className={`w-4 h-4 ${result.katlas_signed ? 'text-emerald-400' : 'text-slate-400'}`} />
        Provenance
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="space-y-2 text-sm">
        <div className="flex justify-between items-center">
          <span className="text-slate-400">Signed:</span>
          <span className={result.katlas_signed ? 'text-emerald-400' : 'text-slate-500'}>
            {result.katlas_signed ? 'Yes' : 'No'}
          </span>
        </div>
        {result.receipt_hash && (
          <div className="flex justify-between items-center">
            <span className="text-slate-400">Receipt:</span>
            <code className="text-cyan-400 text-xs">{result.receipt_hash.substring(0, 20)}...</code>
          </div>
        )}
        <div className="flex justify-between items-center">
          <span className="text-slate-400">Evidence Chain:</span>
          <span className="text-white">{result.evidence_chain_length} steps</span>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Test Metadata Card
export const TestMetadataCard = ({ result }) => (
  <Card className="bg-slate-800/50 border-slate-700">
    <CardHeader className="pb-2">
      <CardTitle className="text-white text-base flex items-center gap-2">
        <FileCheck className="w-4 h-4 text-cyan-400" />
        Test Metadata
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-xs text-slate-500">Test Run ID</div>
          <code className="text-cyan-400">{result.test_run_id}</code>
        </div>
        <div>
          <div className="text-xs text-slate-500">Timestamp</div>
          <span className="text-white">{new Date(result.timestamp).toLocaleString()}</span>
        </div>
        <div>
          <div className="text-xs text-slate-500">Mobility</div>
          <span className="text-white">{result.parameters_used.mobility_requirement}</span>
        </div>
        <div>
          <div className="text-xs text-slate-500">Severity</div>
          <span className="text-white">{result.parameters_used.disruption_severity}</span>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Combined Results Display
export const ScenarioResults = ({ result }) => {
  if (!result) return null;
  
  return (
    <div className="space-y-4" data-testid="scenario-results">
      <SummaryCard result={result} />
      <AdvisoryProcessingCard result={result} />
      
      <div className="grid grid-cols-2 gap-4">
        <RiskAssessmentCard result={result} />
        <ProvenanceCard result={result} />
      </div>
      
      <TestMetadataCard result={result} />
    </div>
  );
};
